if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mainPrevArrow: this.querySelector('[data-main-gallery-nav="prev"]'),
          mainNextArrow: this.querySelector('[data-main-gallery-nav="next"]'),
          currentPage: this.querySelector('[id^="GalleryViewer"] .slider-counter--current'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initMainImageArrows();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event?.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      initMainImageArrows() {
        if (!this.elements.mainPrevArrow || !this.elements.mainNextArrow || !this.elements.viewer) return;

        this.elements.mainPrevArrow.addEventListener('click', () => this.navigateMainImage(-1));
        this.elements.mainNextArrow.addEventListener('click', () => this.navigateMainImage(1));
      }

      navigateMainImage(direction) {
        if (this.elements.thumbnails) {
          const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
            (thumbnail) => thumbnail.offsetParent !== null
          );
          if (thumbnailItems.length) {
            const activeThumbnailIndex = thumbnailItems.findIndex((thumbnail) =>
              thumbnail.querySelector('button')?.hasAttribute('aria-current')
            );
            const normalizedActiveIndex = activeThumbnailIndex >= 0 ? activeThumbnailIndex : 0;
            const nextIndex = (normalizedActiveIndex + direction + thumbnailItems.length) % thumbnailItems.length;
            const targetMediaId = thumbnailItems[nextIndex]?.dataset?.target;
            if (!targetMediaId) return;

            this.setActiveMedia(targetMediaId, false);
            return;
          }
        }

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (item) => item.offsetParent !== null && item.clientWidth > 0
        );
        if (!mediaItems.length) return;

        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const normalizedActiveIndex = activeIndex >= 0 ? activeIndex : 0;
        const nextIndex = (normalizedActiveIndex + direction + mediaItems.length) % mediaItems.length;
        const targetMediaId = mediaItems[nextIndex]?.dataset?.mediaId;
        if (!targetMediaId) return;

        this.setActiveMedia(targetMediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia);
        this.updateMainCounter(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      updateMainCounter(mediaId) {
        if (!this.elements.currentPage) return;
        const activePosition = this.getMediaPosition(mediaId);
        this.elements.currentPage.textContent = activePosition;
      }

      getMediaPosition(mediaId) {
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          const thumbnailPosition = parseInt(activeThumbnail?.dataset?.mediaPosition, 10);
          if (!Number.isNaN(thumbnailPosition)) return thumbnailPosition;
        }

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const activeIndex = mediaItems.findIndex((item) => item.dataset.mediaId === mediaId);
        return activeIndex >= 0 ? activeIndex + 1 : 1;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
